/**
 * Hook: Mapping workflow — workspace mode switching, mapping generation,
 * materialization lifecycle, and schema drift detection.
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { materializeMappingViews, correctMaterialization } from '../../../services/workspaceApi';
import { triggerToast } from '../../../services/toastBridge';
import { getMappings } from '../../../services/mappingApi';
import type { WorkspaceOperation, WorkspaceTable } from '../../../services/workspaceApi';
import type { Schema } from '../../../services/schemaApi';
import type { CanvasSelection } from '../../../canvas/types';
import type { MappingCanvasDataPayload, EmbeddedMutationHandlers } from '../MappingCanvasContent';
import {
  listMappedWorkspaceViews,
  materializationResultFromWorkspaceTables,
} from '../utils/mappedOutputDiscovery';



export interface MaterializationResult {
  viewDetails: Array<{ view_name: string; target_table: string; ddl: string; column_count: number }>;
  failedViews: Array<{ view: string; target: string; error: string; ddl: string; error_classification?: Record<string, any> }>;
}

interface UseMappingWorkflowArgs {
  mode: 'dag' | 'mapping';
  sessionId: string;
  batchId: string;
  batchStatus?: string;
  sessionMeta?: Record<string, any> | null;
  updateMeta: { mutate: (meta: Record<string, any>) => void; mutateAsync: (meta: Record<string, any>) => Promise<any> };
  sendMessage: { mutate: (msg: string | { message: string; displayText?: string }) => void };
  mappingProgress: { type?: string } | null;
  operations: WorkspaceOperation[];
  workspaceTables: WorkspaceTable[];
}

export function useMappingWorkflow({
  mode,
  sessionId,
  batchId,
  batchStatus,
  sessionMeta,
  updateMeta,
  sendMessage,
  mappingProgress,
  operations,
  workspaceTables,
}: UseMappingWorkflowArgs) {
  const navigate = useNavigate();
  const workspaceMode = mode;
  const [mappingSelection, setMappingSelection] = useState<CanvasSelection | null>(null);
  const [mutationHandlers, setMutationHandlers] = useState<EmbeddedMutationHandlers | null>(null);
  const [mappingCanvasData, setMappingCanvasData] = useState<MappingCanvasDataPayload>({
    mappingResponse: undefined,
    healthData: null,
    healthLoading: false,
    lineageChains: [],
    manualAdjustments: [],
    promptRuns: [],
    edgeFieldMap: {},
  });
  const [materializationResult, setMaterializationResult] = useState<MaterializationResult | null>(null);
  const [correctionInProgress, setCorrectionInProgress] = useState(false);
  const [generateMappingOpen, setGenerateMappingOpen] = useState(false);

  // ── Pre-mapping readiness drawer state ─────────────────────────────
  // The readiness gate used to live as step 3 inside ``GenerateMappingDialog``;
  // it now lives in a persistent right-side drawer the host renders
  // next to the workspace. We hold the operator's mode + table-subset
  // selection here so the drawer's "Start mapping" CTA can fire the
  // original engine/iterative kickoff with the exact same payload the
  // dialog would have used.
  type ReadinessKickoff = {
    schema: Schema;
    mode: 'engine' | 'iterative';
    selectedSourceTables: string[] | undefined;
    selectedTargetTables: string[] | undefined;
  };
  const readinessStorageKey = `workspace:pre-mapping-readiness:${sessionId}:${batchId}`;
  const readStoredReadinessKickoff = (): ReadinessKickoff | null => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.sessionStorage.getItem(readinessStorageKey);
      if (!raw) return null;
      return JSON.parse(raw) as ReadinessKickoff;
    } catch {
      window.sessionStorage.removeItem(readinessStorageKey);
      return null;
    }
  };
  const writeStoredReadinessKickoff = (value: ReadinessKickoff | null) => {
    if (typeof window === 'undefined') return;
    try {
      if (value) {
        window.sessionStorage.setItem(readinessStorageKey, JSON.stringify(value));
      } else {
        window.sessionStorage.removeItem(readinessStorageKey);
      }
    } catch {
      // Ignore sessionStorage failures; the drawer can still live in-memory.
    }
  };
  const [readinessDrawer, setReadinessDrawer] =
    useState<ReadinessKickoff | null>(() => readStoredReadinessKickoff());
  const materializingRef = useRef(false);
  const mountMaterializedRef = useRef(false);

  const targetTableNames = useMemo(() => {
    const raw = sessionMeta?.target_tables;
    return Array.isArray(raw)
      ? raw.filter((t): t is string => typeof t === 'string')
      : [];
  }, [sessionMeta?.target_tables]);

  const expectsMappedOutputs = Boolean(
    sessionMeta?.mapping_complete &&
    (targetTableNames.length > 0 || batchStatus === 'mapped' || batchStatus === 'approved'),
  );

  const mappedViewsInWorkspace = useMemo(
    () => listMappedWorkspaceViews(workspaceTables),
    [workspaceTables],
  );

  useEffect(() => {
    if (!sessionMeta?.mapping_complete) {
      setMaterializationResult(null);
      correctionAttemptedRef.current = false;
    }
  }, [sessionMeta?.mapping_complete]);

  // Hydrate lost in-memory materialization metadata when DuckDB views still exist.
  useEffect(() => {
    if (!sessionMeta?.mapping_complete || materializationResult) return;
    if (mappedViewsInWorkspace.length === 0) return;
    const hydrated = materializationResultFromWorkspaceTables(workspaceTables, targetTableNames);
    if (hydrated) {
      setMaterializationResult(hydrated);
    }
  }, [
    sessionMeta?.mapping_complete,
    materializationResult,
    mappedViewsInWorkspace.length,
    workspaceTables,
    targetTableNames,
  ]);

  // SSE-triggered materialization
  // Track the progress object reference (not just type) so the effect fires
  // on every new mapping_complete event, even if type was already 'mapping_complete'.
  useEffect(() => {
    if (
      mappingProgress?.type === 'mapping_complete' &&
      sessionId &&
      batchId
    ) {
      // If a premature materialization is in progress, let it finish —
      // we'll retry via a short delay to avoid a race.
      if (materializingRef.current) {
        const retryTimer = setInterval(() => {
          if (!materializingRef.current) {
            clearInterval(retryTimer);
            materializingRef.current = true;
            setMaterializationResult(null);
            // Invalidate the persisted flag — fresh mapping needs fresh views.
            updateMeta.mutate({ mapping_views_materialized: false });
            materializeMappingViews(sessionId, { batchId })
              .then((res) => {
                setMaterializationResult({
                  viewDetails: res.view_details ?? [],
                  failedViews: res.failed_views ?? [],
                });
                updateMeta.mutate({ mapping_views_materialized: true });
              })
              .catch(() => {})
              .finally(() => { materializingRef.current = false; });
          }
        }, 3000);
        return () => clearInterval(retryTimer);
      }
      materializingRef.current = true;
      setMaterializationResult(null);
      updateMeta.mutate({ mapping_views_materialized: false });
      materializeMappingViews(sessionId, { batchId })
        .then((res) => {
          setMaterializationResult({
            viewDetails: res.view_details ?? [],
            failedViews: res.failed_views ?? [],
          });
          updateMeta.mutate({ mapping_views_materialized: true });
        })
        .catch((err: unknown) => {
          triggerToast(
            'error',
            'Materialization failed',
            err instanceof Error ? err.message : 'Failed to materialize mapping views.',
          );
        })
        .finally(() => {
          materializingRef.current = false;
        });
    }
  }, [mappingProgress, sessionId, batchId]);

  // Page-load materialization (already-mapped batch)
  //
  // Two mapping modes produce different workspace states:
  //   engine:    Backend recipe exists → call materializeMappingViews to create views
  //   iterative: Agent already created __mapped_*__ tables via map_to_schema → no recipe
  //
  // mapping_mode is set explicitly in workspace_meta when the user picks
  // "Mapping Engine" vs "Iterative (Agent)" in the Choose Mapping Mode dialog.
  //
  // mapping_views_materialized is a session hint, not proof views exist:
  // workspace reconstruction reloads sources but deliberately skips
  // re-materializing __mapped_*__ views. When the operator returns later,
  // views may be gone while the flag is still true — rematerialize then.
  useEffect(() => {
    const viewsMissingAfterResume =
      expectsMappedOutputs &&
      workspaceTables.length > 0 &&
      mappedViewsInWorkspace.length === 0;
    const shouldMaterialize =
      sessionMeta?.mapping_complete &&
      sessionId &&
      batchId &&
      !materializationResult &&
      !materializingRef.current &&
      !mountMaterializedRef.current &&
      (
        !sessionMeta?.mapping_views_materialized ||
        viewsMissingAfterResume
      );

    if (!shouldMaterialize) {
      return;
    }

    mountMaterializedRef.current = true;
    materializingRef.current = true;
    materializeMappingViews(sessionId, { batchId })
      .then((res) => {
        setMaterializationResult({
          viewDetails: res.view_details ?? [],
          failedViews: res.failed_views ?? [],
        });
        const createdCount = res.view_details?.length ?? 0;
        if (createdCount > 0 || (res.failed_views?.length ?? 0) > 0) {
          updateMeta.mutate({ mapping_views_materialized: true });
        } else {
          updateMeta.mutate({ mapping_views_materialized: false });
        }
      })
      .catch((err: unknown) => {
        updateMeta.mutate({ mapping_views_materialized: false });
        triggerToast(
          'warning',
          'Materialization unavailable',
          err instanceof Error ? err.message : 'Could not load materialized views.',
        );
      })
      .finally(() => {
        materializingRef.current = false;
      });
  }, [
    sessionMeta?.mapping_complete,
    sessionMeta?.mapping_views_materialized,
    sessionId,
    batchId,
    materializationResult,
    workspaceTables.length,
    mappedViewsInWorkspace.length,
    expectsMappedOutputs,
    updateMeta,
  ]);

  // Auto-correct failed views in the background (once per mapping lifecycle).
  // Fires after materialization completes with failures. The flag is persisted
  // in session workspace_meta so a page refresh doesn't re-trigger correction
  // for the same already-attempted failures.
  const correctionAttemptedRef = useRef(false);
  useEffect(() => {
    if (
      materializationResult &&
      materializationResult.failedViews.length > 0 &&
      sessionId &&
      batchId &&
      !correctionAttemptedRef.current &&
      !sessionMeta?.correction_attempted
    ) {
      correctionAttemptedRef.current = true;
      updateMeta.mutate({ correction_attempted: true });
      setCorrectionInProgress(true);
      correctMaterialization(sessionId, {
        batchId,
        failedViews: materializationResult.failedViews,
      })
        .then((res) => {
          if (res.status === 'corrected' && res.view_names?.length > 0) {
            // Merge corrected views into the result
            const correctedTargets = new Set(
              (res.view_details ?? []).map((v) => v.target_table),
            );
            setMaterializationResult((prev) => {
              if (!prev) return prev;
              return {
                viewDetails: [
                  ...prev.viewDetails,
                  ...(res.view_details ?? []),
                ],
                failedViews: (res.failed_views ?? []).length > 0
                  ? (res.failed_views ?? [])
                  : prev.failedViews.filter(
                    (f) => !correctedTargets.has(f.target),
                  ),
              };
            });
          }
          // If correction didn't help, leave the result as-is.
          // correctionAttemptedRef prevents retrying.
        })
        .catch((err: unknown) => {
          // Correction failed — leave original result, don't retry.
          triggerToast(
            'info',
            'Auto-correction skipped',
            err instanceof Error ? err.message : 'Could not auto-correct failed views.',
          );
        })
        .finally(() => {
          setCorrectionInProgress(false);
        });
    }
  }, [materializationResult, sessionId, batchId, sessionMeta?.correction_attempted, updateMeta]);

  // Fetch mapping response for target table extraction (fallback)
  const { data: agentModeMappingResponse } = useQuery(
    ['mappings-for-dag', batchId],
    () => getMappings(batchId),
    {
      enabled: !!batchId && !!sessionMeta?.mapping_complete,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  );

  // Extract unique target table names
  const mappingTargetTables = useMemo(() => {
    const mappings = mappingCanvasData.mappingResponse?.mappings ?? agentModeMappingResponse?.mappings;
    if (!mappings?.length) return undefined;
    const tables = new Set<string>();
    for (const m of mappings) {
      if (m.target_table) tables.add(m.target_table);
    }
    return tables.size > 0 ? Array.from(tables).sort() : undefined;
  }, [mappingCanvasData.mappingResponse?.mappings, agentModeMappingResponse?.mappings]);

  // Schema drift detection
  const schemaDriftWarnings = useMemo(() => {
    const mappings = mappingCanvasData.mappingResponse?.mappings;
    if (!mappings?.length || !operations.length) return [];
    const mappedSources = new Set(mappings.map((m: any) => m.source_table));
    const DONE = new Set(['executed', 'promoted', 'corrected', 'completed']);
    return operations
      .filter((op) =>
        op.output_ref &&
        DONE.has(op.status) &&
        !op.exploratory &&
        op.operation_type !== 'schema_map' &&  // schema_map ops ARE the mapping, not drift
        !mappedSources.has(op.output_ref),
      )
      .map((op) => ({
        operationId: op.operation_id,
        outputTable: op.output_ref!,
        title: op.description || op.operation_type,
      }));
  }, [operations, mappingCanvasData.mappingResponse?.mappings]);

  // Callbacks
  const handleOpenMappingEditor = useCallback(() => {
    navigate(`/batches/${batchId}/mapping`);
  }, [navigate, batchId]);

  const handleBackToDag = useCallback(() => {
    navigate(`/batches/${batchId}/agent`);
    setMappingSelection(null);
  }, [navigate, batchId]);

  const handleMappingSelectionChange = useCallback((sel: CanvasSelection | null) => {
    setMappingSelection(sel);
  }, []);

  const handleMappingDataReady = useCallback((data: MappingCanvasDataPayload) => {
    setMappingCanvasData(data);
  }, []);

  const resetMappingState = useCallback(() => {
    setMappingSelection(null);
    setMutationHandlers(null);
    setMappingCanvasData({
      mappingResponse: undefined,
      healthData: null,
      healthLoading: false,
      lineageChains: [],
      manualAdjustments: [],
      promptRuns: [],
      edgeFieldMap: {},
    });
    setMaterializationResult(null);
  }, []);

  const handleOpenGenerateMapping = useCallback(() => {
    setGenerateMappingOpen(true);
  }, []);

  // Bridge: dialog → drawer. Called when the operator finishes step 2
  // (Mode) of the Generate Mapping dialog. We park the kickoff
  // intent here and the drawer renders alongside the workspace.
  const handleContinueToReadiness = useCallback(
    (
      schema: Schema,
      mode: 'engine' | 'iterative',
      selectedSourceTables: string[] | undefined,
      selectedTargetTables: string[] | undefined,
    ) => {
      const nextReadinessDrawer = {
        schema, mode, selectedSourceTables, selectedTargetTables,
      };
      setReadinessDrawer(nextReadinessDrawer);
      writeStoredReadinessKickoff(nextReadinessDrawer);
    },
    [writeStoredReadinessKickoff],
  );

  const closeReadinessDrawer = useCallback(() => {
    setReadinessDrawer(null);
    writeStoredReadinessKickoff(null);
  }, [writeStoredReadinessKickoff]);

  const handleMappingEngineStart = useCallback(
    async (schema: Schema, selectedSourceTables?: string[], selectedTargetTables?: string[]) => {
      setGenerateMappingOpen(false);

      let targetTableNames: string[];
      if (selectedTargetTables) {
        targetTableNames = selectedTargetTables;
      } else {
        const { resolveTargetTableNames } = await import('../../../services/schemaApi');
        targetTableNames = await resolveTargetTableNames(schema);
      }

      await updateMeta.mutateAsync({
        target_schema_id: schema.schema_id,
        target_schema_name: schema.schema_name,
        intent: 'map_to_target',
        mapping_mode: 'engine',
        mapping_complete: true,
        correction_attempted: false,
        target_tables: targetTableNames,
        source_tables: selectedSourceTables,
        mapped_tables: [],
      });

      const { triggerBatchMapping } = await import('../../../services/batchApi');
      await triggerBatchMapping(batchId, {
        source_tables: selectedSourceTables,
        target_tables: selectedTargetTables,
        target_schema_id: schema.schema_id,
      });
    },
    [updateMeta, batchId],
  );

  const handleIterativeStart = useCallback(
    async (schema: Schema, selectedSourceTables?: string[], selectedTargetTables?: string[]) => {
      setGenerateMappingOpen(false);

      let targetTableNames: string[];
      if (selectedTargetTables) {
        targetTableNames = selectedTargetTables;
      } else {
        const { resolveTargetTableNames } = await import('../../../services/schemaApi');
        targetTableNames = await resolveTargetTableNames(schema);
      }

      await updateMeta.mutateAsync({
        target_schema_id: schema.schema_id,
        target_schema_name: schema.schema_name,
        intent: 'map_to_target',
        mapping_mode: 'iterative',
        correction_attempted: false,
        target_tables: targetTableNames,
        source_tables: selectedSourceTables,
      });

      const tableCount = targetTableNames.length;
      const tableSubset = selectedTargetTables
        ? ` (${targetTableNames.length} of ${tableCount} tables selected)`
        : '';
      // Send a concise instruction. The agent's system prompt already reads
      // target_tables / mapped_tables from workspace_meta (set above).
      // displayText shows a short label in the chat bubble.
      sendMessage.mutate({
        message: `Map source data to the "${schema.schema_name}" target schema${tableSubset}.`,
        displayText: `Map to ${schema.schema_name}`,
      });
    },
    [updateMeta, sendMessage],
  );

  const handleMutationHandlersReady = useCallback(
    (handlers: EmbeddedMutationHandlers) => setMutationHandlers(handlers),
    [],
  );

  // Drawer "Start mapping" CTA: fire the engine or iterative kickoff
  // with the exact payload the dialog handed us, then close the drawer.
  const handleReadinessStart = useCallback(async () => {
    const k = readinessDrawer;
    if (!k) return;
    writeStoredReadinessKickoff(null);
    setReadinessDrawer(null);
    if (k.mode === 'engine') {
      await handleMappingEngineStart(
        k.schema, k.selectedSourceTables, k.selectedTargetTables,
      );
    } else {
      await handleIterativeStart(
        k.schema, k.selectedSourceTables, k.selectedTargetTables,
      );
    }
  }, [readinessDrawer, handleMappingEngineStart, handleIterativeStart, writeStoredReadinessKickoff]);

  return {
    workspaceMode,
    mappingSelection,
    mappingCanvasData,
    mutationHandlers,
    materializationResult,
    correctionInProgress,
    generateMappingOpen,
    mappingTargetTables,
    schemaDriftWarnings,
    handleOpenMappingEditor,
    handleBackToDag,
    handleMappingSelectionChange,
    handleMappingDataReady,
    resetMappingState,
    handleMutationHandlersReady,
    handleOpenGenerateMapping,
    handleMappingEngineStart,
    handleIterativeStart,
    closeGenerateMappingDialog: useCallback(() => setGenerateMappingOpen(false), []),
    // ── Pre-mapping readiness drawer ────────────────────────────────
    readinessDrawer,
    handleContinueToReadiness,
    closeReadinessDrawer,
    handleReadinessStart,
  };
}
